import { ICellRendererParams, IHeaderParams } from "ag-grid-community";
import { ArrowUp, Star } from "lucide-react";
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { IgAnalyticsTooltip } from "@intelligems/ig-theme/IgAnalyticsTooltip";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgRadio } from "@intelligems/ig-theme/IgRadio";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { AnalyticsAudienceType } from "@intelligems/ig-types/schemas/analytics/base";
import { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import {
  DataType,
  GraphConfig,
} from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DashboardPageName } from "@src/api/types/dashboards";
import { checkBooleanScope } from "@src/components/LimitAccess/check-scopes";
import {
  calculateEstImpactTb,
  findTooltip,
} from "@src/features/analytics/baseDashboard/utils";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { UpliftChip } from "@src/features/analytics/tables/analyticsAudienceTable/UpliftChip";
import {
  getMetricCategoryName,
  getStandardMetricByAnalyticsMetricId,
  getTabName,
  isRolloutGroupDisabled,
  metricsConfig,
} from "@src/features/analytics/tables/analyticsAudienceTable/utils";
import { chartTooltips } from "@src/features/analytics/utils";
import { onPriceTestAction } from "@src/features/experiments/utils/experienceActionUtils";
import { useReadOnlyMode } from "@src/features/publicShareLinks/hooks/useReadOnlyMode";
import { formatOverviewTableFilter } from "@src/features/sharedExperience/components/rollouts/utils";
import { useDialogs } from "@src/hooks/dialog";
import { AppDispatch, useDispatch } from "@src/store";
import {
  getCheckoutBlocksByVariationId,
  getPostPurchaseBlocksByVariationId,
} from "@src/store/slices/checkoutBlocks/selectors";
import { updateDashboardFilter } from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { openDialog } from "@src/store/slices/global";
import { theme } from "@src/theme";
import {
  mpAnalyticsMetricDetailClick,
  mpClickSubpageEntryPoint,
} from "@src/utils/mixpanel";

interface HeaderComponentProps extends IHeaderParams {
  headerLabel: string | undefined;
  index: number;
  pageName: DashboardPageName;
  experienceCategory: ExperienceCategory;
  isPrimary: boolean;
}

export const HeaderComponent = (props: HeaderComponentProps) => {
  const experience = useSelector(selectedExperience);
  return (
    <IgAnalyticsTooltip
      title={
        <IgTypography>
          <IgTypography fontWeight={600}>
            {props.headerLabel ?? "Metric"}
          </IgTypography>
          <br />
          {findTooltip(
            props.headerLabel ?? "Metric",
            chartTooltips,
            experience?.category ?? "experiment"
          )}
        </IgTypography>
      }
    >
      <Grid container sx={{ justifyContent: "flex-start" }}>
        <IgTypography
          sx={{
            fontWeight: 600,
            color: theme.palette.text.secondary,
            display: "inline",
            alignItems: "center",
            textAlign: "end",
          }}
        >
          {props.experienceCategory === "experiment" && props.isPrimary && (
            <Star
              size={20}
              color={theme.igPrimitives.amber[400]}
              style={{
                marginRight: "3px",
              }}
            />
          )}
          {props.headerLabel ?? "Metric"}
        </IgTypography>
      </Grid>
    </IgAnalyticsTooltip>
  );
};

export const MetricCellRenderer = (
  params: ICellRendererParams & {
    config: GraphConfig;
    controlVariationId: string;
    tableData: any[];
    storeCurrencyConfig: { locale: string; code: string };
    testDuration: number;
    pageName: DashboardPageName;
    variations: VariationFe[];
    experienceId: string;
    isCustomMetric: boolean;
  }
) => {
  const graphValueFormatter = useGraphValueFormatter();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const {
    data,
    tableData,
    config,
    storeCurrencyConfig,
    testDuration,
    controlVariationId,
    column,
    pageName,
    variations,
    experienceId,
    isCustomMetric,
  } = params;

  if (!data || !column) {
    return (
      <IgTypography variant={"body2"} color={"tertiary"}>
        ---
      </IgTypography>
    );
  }

  const rawValue = params.value?.value;
  const upliftValue = params.value?.uplift?.value;

  const calculatedImpact =
    config.hasImpactCalculation &&
    (!isCustomMetric || data?.variation_id !== controlVariationId)
      ? (() => {
          const metricKey = config?.impactMetric || config.metrics[0];
          const audience = data.audience;

          const audienceRows = tableData.filter(
            (td) => td.audience === audience
          );

          const controlRow = audienceRows.find(
            (td) => td.variation_id === controlVariationId
          );
          const variantRow = audienceRows.find(
            (td) => td.variation_id === data.variation_id
          );

          const controlValue = controlRow?.[metricKey]?.value ?? 0;
          const variantValue = variantRow?.[metricKey]?.value ?? 0;

          const audienceVisitors = audienceRows.reduce(
            (sum, td) => sum + (td?.n_visitors?.value ?? 0),
            0
          );

          return calculateEstImpactTb(
            config,
            audienceVisitors,
            controlValue,
            variantValue,
            storeCurrencyConfig,
            testDuration,
            pageName
          );
        })()
      : undefined;

  const showUplift = upliftValue !== undefined;

  const shouldShowThreeDecimals =
    Math.min(
      ...tableData.map(
        (d) => d[config.impactMetric ?? config.metrics[0]]?.value
      )
    ) < 0.09;

  const onAddKeyMetricClick = () => {
    // TODO
  };

  const onExploreMetricClick = () => {
    dispatch(
      updateDashboardFilter({
        key: "pageName",
        value: getTabName(config.metrics[0]),
      })
    );
  };

  const metricToUse = config.impactMetric ?? config.metrics[0];
  const numerator = getStandardMetricByAnalyticsMetricId(
    metricsConfig[metricToUse]?.numerator
  );
  const denominator = getStandardMetricByAnalyticsMetricId(
    metricsConfig[metricToUse]?.denominator
  );

  return (
    <Grid
      container
      direction="row"
      wrap="nowrap"
      alignItems="center"
      sx={{ justifyContent: "flex-end" }}
    >
      <IgAnalyticsTooltip
        placement={"right"}
        sx={{ color: "white !important", minWidth: "275px" }}
        title={
          data?.variation_id === controlVariationId && !isCustomMetric ? (
            ""
          ) : (
            <Grid container direction={"column"} spacing={1}>
              <Grid>
                <IgTypography
                  sx={{
                    fontWeight: 600,
                  }}
                  color={"dark"}
                >
                  {
                    variations.find(
                      (variation) => variation.id === data.variation_id
                    )?.name
                  }
                </IgTypography>
              </Grid>

              {data?.n_visitors?.value && isCustomMetric ? (
                <Grid container direction={"row"} alignItems={"center"}>
                  <Grid size={6}>
                    <IgTypography color={"dark"}>
                      # Visitors Triggered
                    </IgTypography>
                  </Grid>
                  <Grid sx={{ textAlign: "left" }} size={6}>
                    <IgTypography
                      sx={{
                        fontWeight: 600,
                      }}
                      color={"dark"}
                    >
                      {isCustomMetric
                        ? data[`n_visitors_${config.metrics[0]}`]
                        : data.n_visitors.value}
                    </IgTypography>
                  </Grid>
                </Grid>
              ) : null}

              {numerator && denominator ? (
                <Grid container>
                  <Grid
                    container
                    direction="row"
                    alignItems="center"
                    wrap="nowrap"
                  >
                    <Grid size={6}>
                      <IgTypography color={"dark"}>
                        {numerator?.name}
                      </IgTypography>
                    </Grid>
                    <Grid
                      container
                      alignItems="center"
                      sx={{ justifyContent: "flex-start" }}
                      size={5}
                    >
                      <IgTypography
                        color={"dark"}
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {graphValueFormatter({
                          isAxisTick: false,
                          value: data[numerator.analyticsMetricId]?.value,
                          configDataTypes: [
                            numerator.analyticsDataType,
                          ] as DataType[],
                          decimalAmountShown: 2,
                        })}
                      </IgTypography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    alignItems="center"
                    wrap="nowrap"
                  >
                    <Grid size={6}>
                      <IgTypography color={"dark"}>
                        {denominator?.name}
                      </IgTypography>
                    </Grid>
                    <Grid
                      container
                      alignItems="center"
                      sx={{ justifyContent: "flex-start" }}
                      size={5}
                    >
                      <IgTypography
                        sx={{
                          fontWeight: 600,
                        }}
                        color={"dark"}
                      >
                        {graphValueFormatter({
                          isAxisTick: false,
                          value: data[denominator.analyticsMetricId]?.value,
                          configDataTypes: [
                            denominator.analyticsDataType,
                          ] as DataType[],
                          decimalAmountShown: 2,
                        })}
                      </IgTypography>
                    </Grid>
                  </Grid>
                </Grid>
              ) : upliftValue ? (
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  wrap="nowrap"
                >
                  <Grid size={6}>
                    <IgTypography color={"dark"}>
                      Uplift vs Control
                    </IgTypography>
                  </Grid>
                  <Grid
                    container
                    alignItems="center"
                    sx={{ justifyContent: "flex-start" }}
                    size={5}
                  >
                    {Math.round(upliftValue * 100) === 0 ? null : (
                      <ArrowUp
                        size={16}
                        color={
                          config.impactCalculationColoring === "neutral"
                            ? theme.igIconColors.light
                            : config.impactCalculationColoring === "inverted"
                              ? upliftValue > 0
                                ? theme.igIconColors.error
                                : theme.igIconColors.success
                              : upliftValue > 0
                                ? theme.igIconColors.success
                                : theme.igIconColors.error
                        }
                        style={{
                          marginRight: "3px",
                          transform:
                            upliftValue < 0 ? "rotate(180deg)" : "none",
                        }}
                      />
                    )}
                    <IgTypography
                      color={"dark"}
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {Math.round(upliftValue * 100) === 0
                        ? "No Change"
                        : Math.abs(upliftValue * 100).toFixed(1)}
                      {Math.round(upliftValue * 100) === 0 ? "" : "%"}
                    </IgTypography>
                  </Grid>
                </Grid>
              ) : null}

              {calculatedImpact && (
                <Grid container direction={"row"} alignItems={"center"}>
                  <Grid size={6}>
                    <IgTypography color={"dark"}>
                      Impact vs Control
                    </IgTypography>
                  </Grid>
                  <Grid sx={{ textAlign: "left" }} size={6}>
                    <IgTypography
                      color={"dark"}
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {calculatedImpact}
                    </IgTypography>
                  </Grid>
                </Grid>
              )}

              <Grid
                container
                direction={"row"}
                justifyContent={"space-between"}
                mt={1}
                spacing={0}
              >
                <Grid size={6}>
                  <IgTypography
                    onClick={() => {
                      if (pageName === "overview") {
                        onExploreMetricClick();
                        mpClickSubpageEntryPoint(
                          experienceId,
                          "Audience Table Tooltip",
                          getTabName(config.metrics[0]),
                          config?.impactMetric ?? config.metrics[0]
                        );
                      } else {
                        onAddKeyMetricClick();
                      }
                    }}
                    sx={{
                      textDecoration: "underline",
                      "&:hover": { cursor: "pointer" },
                    }}
                    fontWeight={600}
                  >
                    {pageName !== "overview" && config.canBeKeyMetric
                      ? ""
                      : `Explore ${getMetricCategoryName(config.impactMetric ?? config.metrics[0])} Metric`}
                  </IgTypography>
                </Grid>
                {config.hasTimeseries ||
                config.hasProbability ||
                config.hasValueCI ? (
                  <Grid sx={{ textAlign: "right" }} size={6}>
                    <IgTypography
                      fontWeight={600}
                      color={"dark"}
                      sx={{
                        textDecoration: "underline",
                        "&:hover": { cursor: "pointer" },
                      }}
                      onClick={() => {
                        mpAnalyticsMetricDetailClick(
                          experienceId,
                          "Audience Table Tooltip",
                          config?.impactMetric ?? config.metrics[0]
                        );
                        void navigate(location.pathname + location.search, {
                          state: {
                            targetId: "metric-details-wrapper",
                            targetMetricId: config.metrics[0],
                          },
                          replace: true,
                        });
                      }}
                    >
                      Metric Details
                    </IgTypography>
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
          )
        }
      >
        <Grid>
          {showUplift && upliftValue ? (
            <UpliftChip
              upliftValue={upliftValue}
              impactCalculationColoring={config.impactCalculationColoring}
            />
          ) : null}
          <IgTypography
            sx={{
              fontWeight: 400,
            }}
          >
            {graphValueFormatter({
              isAxisTick: false,
              value: rawValue,
              configDataTypes: config.dataTypes,
              decimalAmountShown: shouldShowThreeDecimals ? 3 : undefined,
            })}
          </IgTypography>
        </Grid>
      </IgAnalyticsTooltip>
    </Grid>
  );
};

export const GroupCellRenderer = (
  params: ICellRendererParams & {
    tabValue: number;
    variations: VariationFe[];
    selectedRowId?: string | null;
    onRowSelected?: (id: string) => void;
    disabled?: boolean;
    disabledReason?: string;
    isRolloutRender?: boolean;
  }
) => {
  const {
    variations,
    tabValue,
    selectedRowId,
    onRowSelected,
    disabled = false,
    disabledReason = "",
    isRolloutRender,
  } = params;

  const handleRadioClick = (e) => {
    e.stopPropagation(); // Prevent row selection event
    if (onRowSelected && !disabled) {
      onRowSelected(params.data.variation_id);
    }
  };

  return (
    <Grid
      container
      direction={"row"}
      wrap={"nowrap"}
      alignItems={"center"}
      justifyContent={"space-between"}
      marginLeft={tabValue === 0 ? (isRolloutRender ? "5px" : "15px") : "35px"}
    >
      {isRolloutRender && (
        <Grid>
          <IgAnalyticsTooltip title={disabledReason}>
            <IgRadio
              size={"small"}
              disabled={disabled}
              checked={params.data.variation_id === selectedRowId}
              onClick={handleRadioClick}
            />
          </IgAnalyticsTooltip>
        </Grid>
      )}
      <Grid container width={"100%"} maxWidth={"150px"} marginLeft="10px">
        <IgAnalyticsTooltip
          title={variations.find((v) => v.id === params.value)?.name || ""}
        >
          <IgTypography
            sx={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              overflowWrap: "break-word",
            }}
          >
            {variations.find((v) => v.id === params.value)?.name || ""}
          </IgTypography>
        </IgAnalyticsTooltip>
      </Grid>
    </Grid>
  );
};

export const TableGenericCellRenderer = (params: ICellRendererParams) => {
  return (
    <Grid container sx={{ justifyContent: "flex-end" }}>
      <IgTypography
        sx={{
          fontWeight: 400,
          textAlign: "flex-end",
        }}
      >
        {params.valueFormatted}
      </IgTypography>
    </Grid>
  );
};

export const TableRolloutButtonRenderer = (
  params: ICellRendererParams & {
    experience: ExperienceFe;
    variations: VariationFe[];
    dispatch: AppDispatch;
    tabValue: number;
    audienceSegmentSelected: AnalyticsAudienceType | undefined;
    hasPersonalizationAccess: boolean;
  }
) => {
  const {
    experience,
    tabValue,
    variations,
    audienceSegmentSelected,
    dispatch,
    hasPersonalizationAccess,
  } = params;
  const { isReadOnly } = useReadOnlyMode();
  const { openDialog } = useDialogs();
  const variationId: string = params?.node?.data?.variation_id ?? "";
  // Count regular + post-purchase blocks to match the old variation.checkoutBlocks.
  const variationCheckoutBlocks = useSelector(
    getCheckoutBlocksByVariationId(variationId)
  );
  const variationPostPurchaseBlocks = useSelector(
    getPostPurchaseBlocksByVariationId(variationId)
  );

  if (isReadOnly) {
    return null;
  }
  const foundVariation = variations.find(
    (variation) => variation.id === params?.node?.data?.variation_id
  );
  const isRolloutDisabled = isRolloutGroupDisabled({
    hasPersonalizationAccess,
    channelOrTrafficSrc:
      audienceSegmentSelected === "source_channel" ||
      audienceSegmentSelected === "source_site"
        ? params.data?.audience
        : undefined,
    purchaseLikelihood:
      audienceSegmentSelected === "purchase_likelihood"
        ? params.data?.audience
        : undefined,
    foundVariation,
    variationHasCheckoutBlocks:
      variationCheckoutBlocks.length + variationPostPurchaseBlocks.length > 0,
    experience,
    tabValue,
  });

  const onRolloutClick = () => {
    if (params?.data) {
      if (
        experience.status !== "ended" &&
        experience.testTypes.hasTestPricing
      ) {
        onPriceTestAction(
          experience,
          "end",
          params.context.reportingCurrency,
          dispatch
        );
      } else if (!experience.testTypes.hasTestPricing) {
        openDialog("rollouts", {
          variationId: foundVariation?.id,
          tableTargeting: formatOverviewTableFilter(
            params.data,
            audienceSegmentSelected
          ),
        });
      }
    }
  };
  return (
    <Grid container sx={{ justifyContent: "flex-end" }}>
      <IgAnalyticsTooltip title={isRolloutDisabled.reason}>
        <IgButton
          onClick={onRolloutClick}
          disabled={isRolloutDisabled.isDisabled}
          variant={"outlined"}
          size={"extraSmall"}
        >
          Roll out
        </IgButton>
      </IgAnalyticsTooltip>
    </Grid>
  );
};
